import { HTMLControl } from './html_control.js';
import { UIQueueable } from '../ui/ui_queueable.js';
import { window } from '../window.js';

/**
 * Modal dialog widget.
 */
class Dialog extends HTMLControl {
  static show(options) {
    return this.get(options).show();
  }

  static get(options) {
    if (!this.dialogInstance) {
      this.dialogInstance = this.create();
    }
    if (options?.data) {
      this.dialogInstance.set(options.data);
    }
    if (options?.onaccept) {
      this.dialogInstance.onaccept = UIQueueable(options.onaccept);
    }
    return this.dialogInstance;
  }

  static create() {
    return window.document.body.appendChild(
      window.document.createElement(this.tagName)
    );
  }

  /**
   * Opens a dialog.
   */
  show() {
    window.document.body.classList.add('noscroll');
    this.hidden = false;

    let button = null;
    for (button of [this.defaultButton, this.acceptButton, this.cancelButton]) {
      if (button && !button.hidden) {
        break;
      }
    }
    if (button) {
      button.focus();
    }

    window.document.addEventListener('focus', this, true);
    window.document.addEventListener('keydown', this);
  }

  /**
   * Closes the dialog.
   */
  hide() {
    if (!this.onhide || this.onhide() !== false) {
      window.document.removeEventListener('focus', this, true);
      window.document.removeEventListener('keydown', this);
      this.hidden = true;
      window.document.body.classList.remove('noscroll');
      return true;
    }
    return false;
  }

  /**
   * Return true if the dialog is shown.
   */
  isShown() {
    return !this.hidden;
  }

  async accept(ev) {
    let result = await this.checkValidity(ev);
    if (result === false) {
      return result;
    }
    await this.accepted();
    await (this.onaccept && this.onaccept(this.data));
    this.hide();
    return result;
  }

  async cancel() {
    await this.cancelled();
    await (this.oncancel && this.oncancel());
    return this.hide();
  }

  // Protected

  checkValidity() {
    return true;
  }

  accepted() {}

  cancelled() {}

  static get selectors() {
    const Dialog = '.dialog';
    const Buttons = `${Dialog} > .dialog-buttons`;
    return {
      AcceptButton: `${Buttons} .dialog-accept`,
      Body: `${Dialog} > .dialog-body`,
      Buttons: `${Dialog} > .dialog-buttons`,
      CancelButton: `${Buttons} .dialog-cancel`,
      CloseButton: `${Dialog} > .sqbutton`,
      Dialog,
      Title: `${Dialog} > .dialog-title`,
    };
  }

  static get elements() {
    let s = this.selectors;
    return {
      acceptButton: s.AcceptButton,
      bodyElement: s.Body,
      cancelButton: s.CancelButton,
      closeButton: s.CloseButton,
      dialog: s.Dialog,
    };
  }

  static titleMarkup = '';
  static bodyMarkup = '';
  static buttons = {};

  constructor() {
    super();

    this.onaccept = null;
    this.oncancel = null;
    this.onhide = null;

    this.addEventListener(
      'keydown',
      ev => {
        if (!this.isShown()) {
          return;
        }
        switch (ev.key) {
          case 'Escape':
            this.hide();
            ev.stopPropagation();
            break;

          default:
            break;
        }
      },
      false
    );

    this.addEventListener(
      'click',
      ev => {
        if (this.isShown() && ev.target == this) {
          this.cancel();
        }
      },
      false
    );
  }

  initialize() {
    super.initialize();

    this.hidden = true;
    this.classList.add('dialog-background');

    this.closeButton.addEventListener('click', () => this.cancel(), false);
    if (this.acceptButton) {
      this.#addButtonEventListeners(this.acceptButton, ev => this.accept(ev));
    }
    if (this.cancelButton) {
      this.#addButtonEventListeners(this.cancelButton, () => this.cancel());
    }
  }

  static get markup() {
    return `
<div class='dialog'>
  <div class='sqbutton close-button' title='Close'></div>
  <div class='dialog-title'>${this.titleMarkup}</div>
  <div class='dialog-body'>${this.bodyMarkup}</div>
  <div class='dialog-buttons'>${this.buttonsMarkup}</div>
</div>
    `;
  }

  static get buttonsMarkup() {
    let markup = '';
    for (let type in this.buttons) {
      let button = this.buttons[type];
      let classList = ['dialog-button', `dialog-${type}`];
      if (button.default) {
        classList.push('default-button');
      }
      let imageMarkup = '';
      if (button.image) {
        imageMarkup = `
          <img src='${button.image}' alt='' class='button-image'>
        `;
      }
      markup += `
        <span tabindex='0'
              class='${classList.join(' ')}'>
          ${imageMarkup}
          <span class='button-label'>${button.label}</span>
        </span>
      `;
    }
    return markup;
  }

  // EventListener

  handleEvent(ev) {
    switch (ev.type) {
      case 'focus':
        return this.#handleFocus(ev);
      case 'keydown':
        return this.#handleKey(ev);
    }
  }

  // Private

  #addButtonEventListeners(button, callback) {
    button.addEventListener('click', () =>
      this.#onButtonClick(button, callback)
    );
    button.addEventListener('keypress', ev => {
      if (ev.key == ' ' || ev.key == 'Enter') {
        this.#onButtonClick(button, callback);
      }
    });
  }

  async #onButtonClick(button, callback) {
    button.setAttribute('disabled', true);
    await callback();
    button.removeAttribute('disabled');
  }

  #handleFocus(ev) {
    let pos = this.dialog.compareDocumentPosition(ev.target);
    switch (pos) {
      case Node.DOCUMENT_POSITION_PRECEDING:
        ev.preventDefault();
        ev.stopPropagation();
        if (this.#forwardTab) {
          // Tab: move focus into the dialog.
          this.#firstFocuableElement.focus();
        } else {
          // Shift tab: move focus to the document.
          window.document.documentElement.focus();
        }
        break;

      case Node.DOCUMENT_POSITION_FOLLOWING:
        // Tab: keep focus in the dialog, shift tab: focus the dialog.
        ev.preventDefault();
        ev.stopPropagation();
        if (this.#forwardTab) {
          // Tab: the focus should go outside the document, but since there's
          // no technical way how to do this, then let's lock the focus
          // inside the dialog and. move it to the first focusable element.
          this.#firstFocuableElement.focus();
        } else {
          // Shift tab: focus goes into the dialog, focus the last element.
          this.#lastFocuableElement.focus();
        }
        break;

      default:
        // Node.DOCUMENT_POSITION_CONTAINS:
        // Do nothing.
        break;
    }
  }

  #handleKey(ev) {
    if (ev.key == 'Tab') {
      this.#forwardTab = !ev.shiftKey;
    }
  }

  get #firstFocuableElement() {
    // Find element with `autofocus` attribute.
    let target = this.dialog.querySelector('[autofocus]:not([disabled])');
    if (target) {
      return target;
    }

    // Note that this is 'any focusable area'. This list is probably not
    // exhaustive, but the alternative involves stepping through and trying to
    // focus everything.
    let opts = ['button', 'input', 'keygen', 'select', 'textarea'];
    let query = opts.map(el => `${el}:not([disabled])`);
    query.push('[tabindex]:not([disabled]):not([tabindex=""])'); // tabindex != "", not disabled
    return this.dialog.querySelector(query.join(', '));
  }

  get #lastFocuableElement() {
    let opts = ['button', 'input', 'keygen', 'select', 'textarea'];
    let query = opts.map(el => `${el}:not([disabled])`);
    query.push('[tabindex]:not([disabled]):not([tabindex=""])'); // tabindex != "", not disabled
    let targets = this.dialog.querySelectorAll(query.join(', '));
    return targets.length > 0 ? targets[targets.length - 1] : null;
  }

  #forwardTab;
}

export { Dialog };
